import React, { useEffect, useRef, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { CurrencyEnum } from '../../../../lib/enums/currency.enum';
import { OrderSideEnum } from '../../../../lib/enums/order-side.enum';
import lang from '../../../../lib/language';
import { FXSubscriptionService } from '../../../../lib/services/subscription/FXSubscription.service';
import { CurrencySymbolType } from '../../../../lib/services/subscription/types';
import { RootState } from '../../../../lib/store/index';
import { getAvailableEuro, getAvailableUsd, getToken } from '../../../../lib/store/selectors';
import FxState from '../../../../lib/store-util/FxState';
import { formatCurrency, formatNumber, roundNumberToTwoDecimals } from '../../../../lib/util/DataHelpers';
import { NullableString } from '../../../../lib/util/types';

import IconDown from '../../../../assets/img/wallet/icon_down.svg';
import EuroIcon from '../../../../assets/img/wallet/icon_euro_middle.svg';
import IconUp from '../../../../assets/img/wallet/icon_up.svg';
import UsdIcon from '../../../../assets/img/wallet/icon_usa_middle.svg';
import { ButtonTypesEnum } from '../../../../components/Button/Button.types';
import { Button, ClickableImage } from '../../../../components/index';

import styles from './FXOrder.module.scss';

const initialPrice = 1;

type Props = {
    reviewButtonStyle: string;
    showReviewModal: boolean;
    setShowReviewModal: () => void;
    setOrderSide: (side: OrderSideEnum) => void;
    setEurCurrnecy: (currency: string) => void;
    setUsdCurrnecy: (currency: string) => void;
    closeOrderModal?: () => void;
    children?: JSX.Element;
}

const FXOrder = ({
  reviewButtonStyle,
  showReviewModal,
  setShowReviewModal,
  setOrderSide,
  setEurCurrnecy,
  setUsdCurrnecy,
  closeOrderModal,
  children,
}: Props) => {
  const eurRef = useRef<any>();
  const usdRef = useRef<any>();
  const fxRateSymbol = CurrencySymbolType.EuroUSDollar;
  const fxState = FxState.useCustom(fxRateSymbol);
  const askPrice = fxState?.askPrice ?? 0;
  const bidPrice = fxState?.bidPrice ?? 0;

  const [eurValue, setEurValue] = useState<number | string>(1);
  const [usdValue, setUsdValue] = useState<number | string>(roundNumberToTwoDecimals(bidPrice));
  const [euroDirection, setEuroDirection] = useState<'+' | '-'>('+');
  const [usdDirection, setUsdDirection] = useState<'+' | '-'>('-');
  const [currentSide, setCurrentSide] = useState<OrderSideEnum>(OrderSideEnum.Sell);
  const [buyValError, setBuyValError] = useState<boolean>(false);
  const [sellValError, setSellValError] = useState<boolean>(false);
  const [disableExchangeBtn, setDisableExchangeBtn] = useState<boolean>(false);
  const [currentTarget, setCurrentTarget] = useState<CurrencyEnum>(CurrencyEnum.EUR);
  const [fxRate, setFxRate] = useState<number>(bidPrice);

  const accessToken = useSelector<RootState, NullableString>((state: RootState) => getToken(state));
  const availableEuro = useSelector((state: RootState) => roundNumberToTwoDecimals(getAvailableEuro(state)));
  const availableUsd = useSelector((state: RootState) => roundNumberToTwoDecimals(getAvailableUsd(state)));

  useEffect(() => {
    if (fxRate === 0) {
      setFxRate(currentSide === OrderSideEnum.Buy ? askPrice : bidPrice);
      setUsdValue(roundNumberToTwoDecimals(bidPrice));
    }
  }, [currentSide, fxRate, askPrice, bidPrice]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      eurRef.current && eurRef.current.focus();
    }, 1);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!showReviewModal) {
      eurRef.current.disabled = false;
      usdRef.current.disabled = false;
    }
  }, [showReviewModal]);

  useEffect(() => {
    const service = FXSubscriptionService.getInstance(accessToken!);
    service.subscribe(fxRateSymbol);

    return () => {
      service.unsubscribe(fxRateSymbol);
    };
  }, [fxRateSymbol, accessToken]);

  useEffect(() => {
    if (Number(usdValue) === availableUsd && currentSide === OrderSideEnum.Buy) {
      setDisableExchangeBtn(true);
    }
    if (Number(eurValue) === availableEuro && currentSide === OrderSideEnum.Sell) {
      setDisableExchangeBtn(true);
    }
  }, [availableEuro, availableUsd, currentSide, eurValue, usdValue]);

  useEffect(() => {
    if (currentSide === OrderSideEnum.Sell) {
      setBuyValError(false);
      setSellValError(eurValue > availableEuro);
    }
    if (currentSide === OrderSideEnum.Buy) {
      setSellValError(false);
      setBuyValError(usdValue > availableUsd);
    }
  }, [availableEuro, availableUsd, currentSide, eurValue, usdValue]);

  useEffect(() => {
    if (currentSide === OrderSideEnum.Sell) {
      setEuroDirection('-');
      setUsdDirection('+');
      setFxRate(bidPrice);
    } else if (currentSide === OrderSideEnum.Buy) {
      setEuroDirection('+');
      setUsdDirection('-');
      setFxRate(askPrice);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSide]);

  const onExchangeAll = () => {
    if (currentSide === OrderSideEnum.Buy) {
      setUsdValue(availableUsd);
      const currentEurValue = availableUsd * askPrice;
      const currentEurValueRounded = roundNumberToTwoDecimals(currentEurValue);
      setEurValue(currentEurValueRounded);
    } else if (currentSide === OrderSideEnum.Sell) {
      const currentUsdValue = availableEuro * bidPrice;
      const currentUsdValueRounded = roundNumberToTwoDecimals(currentUsdValue);
      setUsdValue(currentUsdValueRounded);
      setEurValue(availableEuro);
    }
    setDisableExchangeBtn(true);
  };

  const checkIfExchangeBtnIsClicked = () => {
    if (disableExchangeBtn) {
      setDisableExchangeBtn(false);
    }
  };

  const onEuroChange = (amount: string) => {
    const currentUsdValue = Number(amount) * fxRate;
    const currentUsdValueRounded = roundNumberToTwoDecimals(currentUsdValue);

    setEurValue(amount);
    setUsdValue(currentUsdValueRounded);
    setCurrentTarget(CurrencyEnum.EUR);
    checkIfExchangeBtnIsClicked();
  };

  const onUsdChange = (amount: string) => {
    const currentEurValue = Number(amount) / fxRate;
    const currentEurValueRounded = roundNumberToTwoDecimals(currentEurValue);

    setUsdValue(amount);
    setEurValue(currentEurValueRounded);
    setCurrentTarget(CurrencyEnum.USD);
    checkIfExchangeBtnIsClicked();
  };

  const changeDirection = () => {
    setDisableExchangeBtn(false);
    if (currentTarget === CurrencyEnum.EUR) {
      setUsdValue(roundNumberToTwoDecimals(
        Number(eurValue) * (currentSide === OrderSideEnum.Sell ? askPrice : bidPrice),
      ));
    }
    if (currentTarget === CurrencyEnum.USD) {
      setEurValue(roundNumberToTwoDecimals(
        Number(usdValue) / (currentSide === OrderSideEnum.Sell ? askPrice : bidPrice),
      ));
    }
    setCurrentSide(currentSide === OrderSideEnum.Buy ? OrderSideEnum.Sell : OrderSideEnum.Buy);
  };

  const onReviewOrder = () => {
    eurRef.current.disabled = true;
    usdRef.current.disabled = true;

    setShowReviewModal();
    setCurrentSide(currentSide);
    setOrderSide(currentSide);
    setUsdCurrnecy(usdValue.toString());
    setEurCurrnecy(eurValue.toString());
    closeOrderModal && closeOrderModal();
  };

  // only re-focus if the user clicked on input
  const checkIfTargetIsInput = (ev: React.FocusEvent<HTMLInputElement, Element>) => {
    if (ev.relatedTarget?.tagName.toLowerCase() !== 'input') {
      ev.target.focus();
    }
  };

  return (
    <>
      <h5 className={styles.header}>
        <span>{currentSide}</span>
        {' '}
        <span>{CurrencyEnum.EUR}</span>
      </h5>
      <div className={styles.exchangeWrapper}>
        <span className={styles.exchangeRate}>
          {`1 ${CurrencyEnum.EUR} = ${fxRate.toFixed(4)} ${CurrencyEnum.USD}`}
        </span>
        <Button
          variant={ButtonTypesEnum.Link}
          onClick={onExchangeAll}
          disabled={disableExchangeBtn}
        >
          {lang.walletExchangeAll()}
        </Button>
      </div>
      <div className={styles.currencyExcangeWrapper}>
        <div className={styles.currencyRow}>
          <div className={styles.currencyInfo}>
            <img src={EuroIcon} alt="exchange euro icon" />
            <div className={styles.balanceWrapper}>
              <div>{CurrencyEnum.EUR}</div>
              <div className={styles.balance}>
                {sellValError ? (
                  <span className={styles.validationError}>
                    <span className={styles.errorText}>
                      {lang.walletExchangeExceedsBalance(`€${formatNumber(availableEuro)}`)}
                    </span>
                  </span>
                ) : (
                  <span>
                    {lang.walletExchangeBalance(`€${formatNumber(availableEuro)}`)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={classNames(styles.currencyValueWrapper, { [styles.validationError]: sellValError })}>
            <CurrencyInput
              ref={eurRef}
              required
              defaultValue={initialPrice}
              className={styles.input}
              allowNegativeValue={false}
              prefix={`${euroDirection}€`}
              value={eurValue}
              onBlur={checkIfTargetIsInput}
              onValueChange={(amount) => {
                onEuroChange(amount ?? '0');
              }}
            />
          </div>
        </div>
        <div className={styles.switchDirection}>
          <div className={styles.horizontalLine} />
          <ClickableImage
            alt="switch currency exchange direction"
            onClick={changeDirection}
            src={currentSide === OrderSideEnum.Buy ? IconUp : IconDown}
          />
        </div>
        <div className={styles.currencyRow}>
          <div className={styles.currencyInfo}>
            <img src={UsdIcon} alt="exchange usd icon" />
            <div className={styles.balanceWrapper}>
              <div>{CurrencyEnum.USD}</div>
              <div className={styles.balance}>
                {buyValError ? (
                  <span className={styles.validationError}>
                    <span className={styles.errorText}>
                      {lang.walletExchangeExceedsBalance(formatCurrency(availableUsd))}
                    </span>
                  </span>
                ) : (
                  <span>{lang.walletExchangeBalance(formatCurrency(availableUsd))}</span>
                )}
              </div>
            </div>
          </div>
          <div className={classNames(styles.currencyValueWrapper, { [styles.validationError]: buyValError })}>
            <CurrencyInput
              ref={usdRef}
              defaultValue={bidPrice}
              onValueChange={(amount) => {
                onUsdChange(amount ?? '0');
              }}
              required
              className={styles.input}
              allowNegativeValue={false}
              prefix={`${usdDirection}$`}
              value={usdValue}
              onBlur={checkIfTargetIsInput}
            />
          </div>
        </div>
      </div>
      <div className={styles.feeMessage}>{lang.walletExchangeFeeMessage()}</div>
      {children}
      <Button
        className={reviewButtonStyle}
        disabled={buyValError || sellValError || !usdValue || !eurValue || !askPrice || !bidPrice}
        fullWidth
        onClick={onReviewOrder}
      >
        {lang.walletExchangeReviewBtn()}
      </Button>
    </>
  );
};

export default FXOrder;
